/**
 * The teleprompter's arithmetic.
 *
 * A therapist reading a meditation aloud cannot also be scrolling, and cannot
 * lose their place — the pauses are as much the work as the words, so a script
 * that jumps or races is worse than no prompter at all. Everything here is a
 * pure function of the script and the pace, so the timing can be checked
 * without a browser, a clock or a call.
 *
 * Time is apportioned by *word count*, not by line: a long paragraph and a
 * one-word line are not read for the same length of time, and giving each line
 * an equal slice is what makes prompters lurch.
 */

/** A pause after a paragraph, in words' worth of silence. */
const PARAGRAPH_PAUSE_WORDS = 4;

export type PromptSegment = {
    /**
     * A stretch to read, or a silence to hold.
     *
     * A silence is a segment of its own rather than a property of the
     * paragraph before it: the prompter has to sit still on it, visibly, for
     * as long as it lasts — a therapist watching a marker that has stopped
     * needs to know it stopped on purpose.
     */
    kind: "text" | "pause" | "gong";
    /** The paragraph as it will be shown; empty for a silence. */
    text: string;
    /** Words in it, which is what its share of the time is based on. */
    words: number;
    /** For a silence, how long it lasts. */
    pauseSeconds?: number;
    /** Seconds from the start of the read at which this paragraph begins. */
    startSeconds: number;
    /** Seconds at which the next one does. */
    endSeconds: number;
};

/**
 * What a therapist can write into a script besides words.
 *
 * Two, and only two, because a tag has to *do* something: `[10s]` holds the
 * prompter for ten seconds, `[gong]` sounds the singing bowl in the room. A
 * tag with no mechanism behind it would be a comment pretending to be an
 * instruction, which is worse than writing the instruction as a comment.
 *
 * Both are matched in one pass so that the order they were written in is the
 * order they happen in.
 */
const TAG = /\[\s*(?:(\d+)\s*(s|m|seg|segundos?|min|minutos?)?|(gong|campana|cuenco))\s*\]/gi;

/** Seconds a tag asks for, capped so a typo cannot stall a session for an hour. */
const MAX_PAUSE_SECONDS = 600;

const pauseSecondsFrom = (amount: string, unit?: string): number => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return 0;
    const minutes = Boolean(unit) && /^m/i.test(unit as string);
    return Math.min(MAX_PAUSE_SECONDS, Math.round(minutes ? value * 60 : value));
};

export type ScriptBlock =
    | { kind: "text"; text: string }
    | { kind: "pause"; seconds: number }
    | { kind: "gong" };

/**
 * The script as a run of things to read, silences to hold and bowls to sound.
 *
 * Tags are taken out of the text they were written in: a paragraph reads as
 * the therapist wrote it, and the pause that followed the sentence becomes its
 * own beat rather than a stray `[10s]` on screen mid-meditation.
 */
export function splitIntoBlocks(script: string): ScriptBlock[] {
    const blocks: ScriptBlock[] = [];

    let lastIndex = 0;
    const pushText = (raw: string) => {
        for (const text of splitIntoParagraphs(raw)) {
            blocks.push({ kind: "text", text });
        }
    };

    TAG.lastIndex = 0;
    let match = TAG.exec(script);
    while (match !== null) {
        pushText(script.slice(lastIndex, match.index));
        if (match[3]) {
            blocks.push({ kind: "gong" });
        } else {
            const seconds = pauseSecondsFrom(match[1], match[2]);
            if (seconds > 0) blocks.push({ kind: "pause", seconds });
        }
        lastIndex = match.index + match[0].length;
        match = TAG.exec(script);
    }
    pushText(script.slice(lastIndex));

    return blocks;
}

/**
 * Split a script into the blocks the prompter shows one at a time.
 *
 * Blank lines separate paragraphs — those are the beats. Line breaks *inside*
 * a paragraph are kept as they were typed: a therapist writing a meditation
 * breaks lines where they want to breathe, and running them together hands
 * them back a wall of text to read from at arm's length. Only runs of spaces
 * and tabs are tidied.
 */
export function splitIntoParagraphs(script: string): string[] {
    return script
        .split(/\n[ \t]*\n\s*/)
        .map((block) =>
            block
                .replace(/[ \t]+/g, " ")
                .replace(/[ \t]*\n[ \t]*/g, "\n")
                .trim()
        )
        .filter((block) => block.length > 0);
}

export function countWords(text: string): number {
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
}

/**
 * Lay the script out on a timeline at the given reading pace.
 *
 * A short pause is added after every paragraph but the last, priced in words
 * so it scales with the pace: a therapist who slows the whole reading down
 * means the silences too. A silence the therapist wrote takes the seconds they
 * asked for and no others — `[10s]` is ten seconds at any pace, because it was
 * chosen for the meditation rather than for the reading.
 */
export function paceScript(
    script: string | null | undefined,
    wordsPerMinute: number
): PromptSegment[] {
    if (!script) return [];
    const blocks = splitIntoBlocks(script);
    if (blocks.length === 0) return [];

    const pace = wordsPerMinute > 0 ? wordsPerMinute : 1;
    const secondsPerWord = 60 / pace;

    const segments: PromptSegment[] = [];
    let cursor = 0;

    blocks.forEach((block, index) => {
        if (block.kind === "gong") {
            // No length of its own: the strike sounds *at* this point in the
            // reading, and the silence around it is whatever the therapist
            // wrote around it.
            segments.push({
                kind: "gong",
                text: "",
                words: 0,
                startSeconds: cursor,
                endSeconds: cursor,
            });
            return;
        }
        if (block.kind === "pause") {
            segments.push({
                kind: "pause",
                text: "",
                words: 0,
                pauseSeconds: block.seconds,
                startSeconds: cursor,
                endSeconds: cursor + block.seconds,
            });
            cursor += block.seconds;
            return;
        }

        const words = countWords(block.text);
        // No trailing breath before a silence the therapist asked for, or at
        // the end: the pause they wrote is the pause they meant.
        const next = blocks[index + 1];
        const pause =
            next === undefined || next.kind === "pause" || next.kind === "gong"
                ? 0
                : PARAGRAPH_PAUSE_WORDS;
        const duration = (words + pause) * secondsPerWord;
        segments.push({
            kind: "text",
            text: block.text,
            words,
            startSeconds: cursor,
            endSeconds: cursor + duration,
        });
        cursor += duration;
    });

    return segments;
}

/** How long the whole script takes at this pace, in seconds. */
export function totalReadingSeconds(segments: PromptSegment[]): number {
    if (segments.length === 0) return 0;
    return segments[segments.length - 1].endSeconds;
}

/**
 * The strikes to sound between one moment of the reading and the next.
 *
 * Asked for a window rather than a point, because the clock arrives in ticks:
 * a gong written between two of them would otherwise never sound. Seeking
 * backwards and reading a passage again sounds it again, which is what the
 * therapist asked for by going back.
 */
export function gongsBetween(
    segments: PromptSegment[],
    fromSeconds: number,
    toSeconds: number
): number[] {
    if (toSeconds <= fromSeconds) return [];
    return segments
        .map((segment, index) => ({ segment, index }))
        .filter(
            ({ segment }) =>
                segment.kind === "gong" &&
                segment.startSeconds > fromSeconds &&
                segment.startSeconds <= toSeconds
        )
        .map(({ index }) => index);
}

/**
 * Which paragraph should be lit at this moment.
 *
 * Clamped at both ends: before the read starts the first paragraph is the one
 * to look at, and after it finishes the last one stays lit rather than the
 * prompter going blank while the therapist is still speaking.
 */
export function activeSegmentIndex(
    segments: PromptSegment[],
    elapsedSeconds: number
): number {
    if (segments.length === 0) return -1;
    if (elapsedSeconds <= 0) return 0;

    for (let index = 0; index < segments.length; index += 1) {
        if (elapsedSeconds < segments[index].endSeconds) return index;
    }
    return segments.length - 1;
}

/**
 * How many words of a paragraph are due to have been said by now.
 *
 * The paragraph is lit as a whole, and within it the words already due are
 * coloured, so the therapist can see the line they should be on rather than
 * the block they are somewhere inside. Words are timed evenly across the
 * paragraph's share of the clock — nobody reads that evenly, which is why the
 * colour is a guide and tapping a paragraph still moves the marker.
 */
export function wordsDue(segment: PromptSegment, elapsedSeconds: number): number {
    const span = segment.endSeconds - segment.startSeconds;
    if (segment.words <= 0) return 0;
    if (span <= 0 || elapsedSeconds >= segment.endSeconds) return segment.words;
    if (elapsedSeconds <= segment.startSeconds) return 0;

    const through = (elapsedSeconds - segment.startSeconds) / span;
    return Math.min(segment.words, Math.floor(through * segment.words) + 1);
}

/** How far through the read we are, 0–1, for the progress bar. */
export function readingProgress(
    segments: PromptSegment[],
    elapsedSeconds: number
): number {
    const total = totalReadingSeconds(segments);
    if (total <= 0) return 0;
    return Math.min(1, Math.max(0, elapsedSeconds / total));
}

/**
 * Where to jump to in order to start reading a given paragraph.
 *
 * The prompter is a guide, not a metronome: a therapist who has drifted needs
 * to be able to put the marker back where they actually are with one tap.
 */
export function seekToSegment(
    segments: PromptSegment[],
    index: number
): number {
    if (segments.length === 0) return 0;
    const clamped = Math.min(Math.max(index, 0), segments.length - 1);
    return segments[clamped].startSeconds;
}
